import React, { useState } from 'react';
import { connectionTester, type ConnectionTestResult } from '@/utils/testConnections';
import AuthTester from './AuthTester';

export const ConnectionTester: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<ConnectionTestResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState<'connections' | 'auth'>('connections');

  const runTests = async () => {
    setIsLoading(true);
    setShowResults(true);
    
    try {
      console.log('🧪 Starting connection tests...');
      
      // Test service endpoints
      const serviceResults = await connectionTester.testAllConnections();
      console.log('📊 Service test results:', serviceResults);
      
      // Test health endpoints
      const healthResults = await connectionTester.testHealthEndpoints();
      console.log('🏥 Health test results:', healthResults);
      
      const allResults = [...serviceResults, ...healthResults];
      setResults(allResults);
      
      console.log('✅ All tests completed:', allResults);
      
    } catch (error) {
      console.error('❌ Test runner error:', error);
      // Add error result to show something went wrong
      setResults([{
        service: 'Test Runner',
        endpoint: 'Internal Error',
        status: 'error',
        message: `❌ Test runner failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: 'success' | 'error') => {
    return status === 'success' ? '✅' : '❌';
  };

  const getStatusColor = (status: 'success' | 'error') => {
    return status === 'success' ? 'text-green-600' : 'text-red-600';
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const successRate = results.length > 0 ? ((successCount / results.length) * 100).toFixed(1) : 0;

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🔗 Backend Service Testing Suite
        </h2>
        <p className="text-gray-600">
          Test backend service connections and authentication flow. All tests require authentication for security.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('connections')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'connections'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔗 Service Connections
            </button>
            <button
              onClick={() => setActiveTab('auth')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'auth'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔐 Authentication Tests
            </button>
          </nav>
        </div>
        
        {/* Tab Descriptions */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          {activeTab === 'connections' ? (
            <p className="text-sm text-gray-600">
              <strong>Service Connections:</strong> Test connectivity to all backend microservices and health endpoints. 
              These tests verify that the frontend can communicate with the API gateway and all backend services.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              <strong>Authentication Tests:</strong> Test your current authentication session, token validation, 
              and backend integration. All tests use your authenticated session - no credentials required.
            </p>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'connections' ? (
        <div>
          <div className="mb-6">
            <button
              onClick={runTests}
              disabled={isLoading}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Testing Connections...
                </>
              ) : (
                '🧪 Run Connection Tests'
              )}
            </button>
          </div>

      {showResults && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">📊 Test Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">{successCount}</div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{successRate}%</div>
                <div className="text-sm text-gray-600">Success Rate</div>
              </div>
            </div>
          </div>

          {/* Results by Service */}
          {results.length > 0 ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">🔧 Detailed Results</h3>
              
              {/* Group results by service */}
              {Object.entries(
                results.reduce((groups, result) => {
                  if (!groups[result.service]) {
                    groups[result.service] = [];
                  }
                  groups[result.service].push(result);
                  return groups;
                }, {} as Record<string, ConnectionTestResult[]>)
              ).map(([service, serviceResults]) => (
                <div key={service} className="mb-4 border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 font-medium">
                    {service}
                  </div>
                  <div className="divide-y">
                    {serviceResults.map((result, index) => (
                      <div key={index} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg">{getStatusIcon(result.status)}</span>
                          <div>
                            <div className="font-medium">{result.endpoint}</div>
                            <div className={`text-sm ${getStatusColor(result.status)}`}>
                              {result.message}
                            </div>
                          </div>
                        </div>
                        {result.responseTime && (
                          <div className="text-sm text-gray-500">
                            {result.responseTime}ms
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">⚠️ No Results</h4>
              <p className="text-sm text-yellow-700">
                No test results were returned. Check the browser console for error messages.
                This could indicate an issue with the testing framework or service imports.
              </p>
            </div>
          )}

          {/* Troubleshooting Tips */}
          {errorCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">🔧 Troubleshooting Tips</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Check if backend services are running and deployed</li>
                <li>• Verify nginx gateway is routing requests correctly</li>
                <li>• Ensure CORS is configured properly on backend services</li>
                <li>• Check browser console for detailed error messages</li>
                <li>• Verify environment variables are set correctly</li>
                <li>• For auth-required endpoints, make sure you're logged in</li>
              </ul>
            </div>
          )}

          {/* Success Message */}
          {errorCount === 0 && results.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">🎉 All Connections Working!</h4>
              <p className="text-sm text-green-700">
                All backend service connections are working properly. Your frontend is successfully 
                connected to all microservices through the nginx gateway.
              </p>
            </div>
          )}
        </div>
      )}

          {/* Instructions */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">📋 How to Use</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Click "Run Connection Tests" to test all service endpoints</li>
              <li>Check the results to see which services are working</li>
              <li>For auth-required tests, make sure you're logged in first</li>
              <li>Check browser console for detailed logs and error messages</li>
              <li>Use the troubleshooting tips if any connections fail</li>
            </ol>
          </div>
        </div>
      ) : (
        <AuthTester />
      )}
    </div>
  );
};

export default ConnectionTester;